import { FC, useContext, useRef } from 'react';

import {
  AddImageIcon,
  BucketIcon,
  Button,
  H3,
  Tooltip,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller } from 'react-hook-form';

import { useUploadFile } from 'hooks';
import { LocalizationContext } from 'localization';

import notification from '../../../../services/notificationService';
import { FILE_MAXIMUM_SIZE } from '../../constants';
import { NewsPreview } from './components/NewsPreview';
import { ImageTooltip } from './ImageTooltip';
import { IRightBlockProps } from './interfaces';
import s from './RightBlock.module.scss';

export const RightBlock: FC<IRightBlockProps> = ({
  control,
  watch,
  linkButton,
}) => {
  const { t } = useContext(LocalizationContext);
  const imgInput = useRef<HTMLInputElement>(null);

  const { uploadImage } = useUploadFile();
  return (
    <div className={s.container}>
      <div className={s.block}>
        <div className={s.top}>
          <H3>{t('cover')}</H3>
          <Tooltip
            header={t('news.image.tooltip.title')}
            content={<ImageTooltip />}
            contentClassName={s.tooltip_content}
            position="bottom-left"
          />
        </div>
        <Controller
          name="image"
          control={control}
          render={({ field: { value, onChange } }) => (
            <div className={cn(s.image)}>
              {value ? (
                <div
                  style={{
                    backgroundImage: `url(${value})`,
                  }}
                  className={s.image_container}
                >
                  <div className={s.image_controls}>
                    <Button
                      iconName="EditIcon"
                      variant="secondary"
                      className={s.button_edit}
                      onClick={() => imgInput.current?.click()}
                    ></Button>

                    <Button
                      variant="secondary"
                      className={s.button_delete}
                      color="red"
                      onClick={() => onChange('')}
                    >
                      <BucketIcon color="error" size="m" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className={s.default_image}
                  onClick={() => {
                    imgInput.current?.click();
                  }}
                >
                  <AddImageIcon size="xxl" />
                  <span className={s.default_text}>
                    {t('news.image.placeholder')}
                  </span>
                </div>
              )}
              <input
                ref={imgInput}
                type="file"
                accept=".png, .jpg"
                className={s.file_input}
                onChange={async (event) => {
                  const file = event.target.files?.[0];

                  if (!file) {
                    return;
                  }

                  if (file.size > FILE_MAXIMUM_SIZE) {
                    notification.info({
                      t,
                      type: 'warning',
                      title: t('errors.upload.size'),
                      message: t('errors.upload.size.description', 2),
                    });
                    return;
                  }

                  try {
                    const imageUrl = await uploadImage(file);
                    onChange(imageUrl);
                  } catch (error) {
                    console.error('Upload failed:', error);
                  }
                }}
              />
            </div>
          )}
        />
      </div>

      <NewsPreview control={control} watch={watch} linkButton={linkButton} />
    </div>
  );
};
